'use client'

import { Minus, Plus } from 'lucide-react'
import { useState } from 'react'

/** Number input with −/+ buttons, clamped to [min, max]. */
export function QuantityStepper({
  id,
  value,
  onChange,
  suffix,
  min = 1,
  max = 999,
}: {
  id: string
  value: number
  onChange: (next: number) => void
  /** Unit shown next to the field, already pluralized by the caller. */
  suffix: string
  min?: number
  max?: number
}) {
  const clamp = (n: number) => Math.min(max, Math.max(min, n))

  /**
   * What the user is mid-way through typing, before it is a usable number.
   *
   * Without this the field desyncs from the price: typing "0" clamps the state
   * to 1, React sees the value it already held, skips the DOM update — and the
   * box keeps showing "0" while the summary charges for 1. The draft holds the
   * literal keystrokes and `onBlur` snaps the display back to the real value.
   */
  const [draft, setDraft] = useState<string | null>(null)

  const step = (delta: number) => {
    setDraft(null)
    onChange(clamp(value + delta))
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => step(-1)}
        aria-label="Zmniejsz"
        className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-white/70 transition hover:border-white/40 hover:text-white"
      >
        <Minus size={14} aria-hidden="true" />
      </button>

      <span className="flex items-baseline gap-1">
        <input
          id={id}
          type="number"
          min={min}
          max={max}
          value={draft ?? value}
          onChange={(e) => {
            setDraft(e.target.value)
            const next = Number.parseInt(e.target.value, 10)
            // An empty or partial entry parses to NaN — keep the last good
            // value priced rather than snapping to min under the user's cursor.
            if (!Number.isNaN(next)) onChange(clamp(next))
          }}
          onBlur={() => setDraft(null)}
          className="w-14 rounded-xl border border-white/15 bg-black/40 px-2 py-1.5 text-center text-[0.95rem] font-semibold text-white outline-none transition focus:border-white/40 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
        />
        <span className="w-16 text-[0.75rem] text-white/55">{suffix}</span>
      </span>

      <button
        type="button"
        onClick={() => step(1)}
        aria-label="Zwiększ"
        className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-white/70 transition hover:border-white/40 hover:text-white"
      >
        <Plus size={14} aria-hidden="true" />
      </button>
    </div>
  )
}
